import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, unwrap } from "../../api/client";
import { meQuery } from "../../api/queries";

/**
 * "Include my use in anonymous usage statistics" (VisionPlatform usage-metrics UM-05): on by default; switched off,
 * vision-api records nothing for the person from the next request and deletes what it holds within 24 h. The choice is
 * stored with the account (`usage_stats_objected_at`), so it follows the person to the phones and other regions. The
 * browser stores nothing for it.
 */
export function UsageStatsSwitch() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useQuery(meQuery());
  const labelId = useId();
  const hintId = useId();
  const save = useMutation({
    mutationFn: (objected: boolean) =>
      unwrap(apiClient("customers").PATCH("/v1/me", { body: { usage_stats_objected: objected } })),
    onSuccess: (data) => queryClient.setQueryData(meQuery().queryKey, data),
  });
  if (!me.data) return null;
  const included = !me.data.usage_stats_objected_at;

  return (
    <div className="mt-2 border-t border-gray-100 px-2 pt-2">
      <div className="flex items-start gap-3">
        <span id={labelId} className="flex-1 text-gray-900">
          {t("user.usageStats")}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={included}
          aria-labelledby={labelId}
          aria-describedby={hintId}
          disabled={save.isPending}
          onClick={() => save.mutate(included)}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${included ? "bg-brand" : "bg-gray-400"}`}
        >
          <span
            aria-hidden="true"
            className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${included ? "translate-x-5" : ""}`}
          />
        </button>
      </div>
      <p id={hintId} className="mt-1 text-xs text-gray-700">
        {t(included ? "user.usageStatsOn" : "user.usageStatsOff")}
      </p>
      {save.isError && (
        <p role="alert" className="mt-1 text-xs text-red-800">
          {t("user.usageStatsError")}
        </p>
      )}
    </div>
  );
}
